import { useMemo } from 'react'
import { Card, CardContent, Typography, Box, Alert } from '@mui/material'
import { aggregateData, AGGREGATION_INTERVALS } from '../utils/chartUtils'
import CustomerPlot from './plotting/CustomerPlot'

function HistoricalGraphs({ data, selectedFields, availableFields, aggregationInterval = 'raw', timezone = 'UTC' }) {
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return []
    const intervalMs = AGGREGATION_INTERVALS[aggregationInterval]
    return aggregateData(data, intervalMs)
  }, [data, aggregationInterval])

  const fields = [
    { key: 'alkalinity_ppm', dataKey: 'total_alkalinity_ppm_rolling_avg', label: 'Total Alkalinity', unit: 'ppm', decimals: 1, color: '#2196f3' },
    { key: 'ph', dataKey: 'ph', label: 'pH', unit: '', decimals: 2, color: '#4caf50' },
    { key: 'temperature', dataKey: 'temperature_c', label: 'Temperature', unit: '°C', decimals: 1, color: '#ff9800' },
    { key: 'conductivity', dataKey: 'conductivity_mscm', label: 'Conductivity', unit: 'mS/cm', decimals: 2, color: '#9c27b0' },
  ]

  const activeFields = fields.filter(f =>
    selectedFields[f.key] && availableFields[f.key]
  )

  if (!data || data.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No data available for selected time range
      </Alert>
    )
  }

  if (activeFields.length === 0) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        Please select at least one field to display
      </Alert>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', mb: 4 }}>
      {activeFields.map(field => {
        const chartData = processedData
          .filter(d => d[field.dataKey] !== undefined && d[field.dataKey] !== null)
          .map(d => ({ value: d[field.dataKey], timestamp: d.timestamp }))
          .sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          )

        if (chartData.length === 0) return null

        return (
          <Card key={field.key} sx={{ width: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                component="div"
                fontWeight="bold"
                gutterBottom
                sx={{ color: field.color, mb: 1 }}
              >
                {field.label}
              </Typography>
              <CustomerPlot
                data={chartData}
                label={field.label}
                unit={field.unit}
                color={field.color}
                decimals={field.decimals}
                timezone={timezone}
              />
            </CardContent>
          </Card>
        )
      })}
    </Box>
  )
}

export default HistoricalGraphs
